// Shared MFA route guard mounted by every PeptideClients SPA (web,
// shop, sell, admin, partners). Sits BETWEEN the per-app `RequireAuth`
// guard and whatever org/store/vendor context routes come next so that:
//
//   - Unauthenticated users keep getting bounced to /sign-in by the
//     per-app `RequireAuth`.
//   - The `/mfa` route itself is mounted OUTSIDE this guard (otherwise:
//     loop -- gate redirects to /mfa, gate sees not-verified, redirects
//     again, ad infinitum).
//   - Authenticated users without 2FA pass straight through.
//   - Authenticated users WITH 2FA but a stale session see /mfa exactly
//     once per 7-day mfa_session window (was 12h before migration
//     20260704001200 — the hourly token-refresh re-check made the 12h
//     expiry feel like a random mid-task bounce to /mfa).
//
// SINGLE 2FA SURFACE: this guard NO LONGER prompts the user to *set up*
// 2FA. Optional 2FA enrollment is owned entirely by the central auth
// site (auth.peptideclients.com) -- showing a second setup wizard here
// is exactly the "two prompts to sign up with 2FA" double-prompt the
// operator reported (2026-06-08). The guard's only jobs now are: allow
// when 2FA is off / already verified / trusted via the cross-origin
// handoff hint, and redirect to /mfa when 2FA is on but this session
// hasn't cleared the OTP yet.
//
// The component is self-contained: it subscribes to supabase-js's auth
// state via `onAuthStateChange` rather than relying on a per-app
// AuthProvider, so each SPA can drop it into the router without
// passing the session via props or context. The MFA-state read goes
// through `readMfaClaims()` which is itself two-stage: JWT claim first
// (fast path), then `core.rpc_user_mfa_status` RPC fallback (catches
// the Custom Access Token Hook not being registered in the production
// Supabase Auth dashboard, which has bitten us before -- see migration
// 20260606020000_*).
//
// Tab-focus safety: this guard is a *route element*, so anything we
// return that isn't `<Outlet />` unmounts the entire matched child
// tree -- including any in-progress form state. The handler below
// therefore mirrors the per-app AuthProviders and gates `setSession`
// through `sessionsEqual` from `@pc/db`, so the no-op `SIGNED_IN`
// that `@supabase/auth-js` re-emits on every `visibilitychange`
// (`_recoverAndRefresh` -> `_notifyAllSubscribers('SIGNED_IN', ...)`)
// is a literal React state no-op. The second effect is
// stale-while-revalidate -- it re-runs only when the session content
// actually changes, and it never flips `decision` back to `pending`
// after the initial bootstrap. If the new claims flip MFA from
// allowed to denied, the gate `<Navigate />`s the user out (an
// intentional redirect, not a Loading flash). If the next maintainer
// reintroduces a raw `setSession(s)` here or re-adds the historical
// `setDecision('pending')` reset, every tab-switch in every SPA
// will reset every in-progress form again.
//
// Caller per-SPA pattern (admin / partners / shop / sell / web all
// look the same):
//
//   import { RequireMfaVerified } from '@pc/ui';
//   ...
//   { path: '/mfa', element: <MfaChallengePage /> },
//   {
//     element: <RequireMfaVerified />,
//     children: [ ...the rest of the gated routes ],
//   }

import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  getSession,
  getMySmsPreferences,
  onAuthStateChange,
  readMfaClaims,
  sessionsEqual,
  type Session,
} from '@pc/db';
import { BootLoader } from '../BootLoader';
import { readMfaLocalHint } from './mfaLocalHint';

interface RequireMfaVerifiedProps {
  /**
   * Where to send the user when they need to clear an MFA challenge.
   * Defaults to `/mfa` since every SPA mounts `<MfaChallengePage />`
   * at that path. Override if a downstream SPA picks a different URL.
   */
  mfaPath?: string;
}

type Decision = 'pending' | 'allow' | 'deny';
const MFA_RETURN_TO_STORAGE_KEY = 'pc.mfa.return_to';

export function RequireMfaVerified({ mfaPath = '/mfa' }: RequireMfaVerifiedProps = {}) {
  const location = useLocation();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [decision, setDecision] = useState<Decision>('pending');

  useEffect(() => {
    let alive = true;
    getSession()
      .then((s) => {
        if (alive) setSession(s);
      })
      .catch(() => {
        if (alive) setSession(null);
      });
    const off = onAuthStateChange((_event, s) => {
      setSession((prev) => {
        const prevSess = prev === undefined ? null : prev;
        return sessionsEqual(prevSess, s) ? prev : s;
      });
    });
    return () => {
      alive = false;
      off();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      // Session resolution still in flight (initial getSession()).
      if (session === undefined) return;
      // No session: let the outer per-app RequireAuth send the user to
      // /sign-in. We don't deny here because that would override the
      // /sign-in redirect with a /mfa redirect.
      if (session === null) {
        if (!cancelled) setDecision('allow');
        return;
      }
      const claims = await readMfaClaims();
      const prefs = await getMySmsPreferences().catch(() => null);
      if (cancelled) return;
      if (!claims) {
        // readMfaClaims returns null only when there's no access token,
        // which contradicts session !== null. Allow as a defensive
        // no-op rather than locking the user out.
        setDecision('allow');
        return;
      }
      // Same-origin handoff hint: when the auth site has just confirmed
      // MFA in THIS turn, it writes a localStorage record keyed by the
      // JWT session_id (see `packages/ui/src/auth/mfaLocalHint.ts`).
      // Trusting that here side-steps the cross-origin RPC race that
      // was bouncing operators between auth.peptideclients.com/login
      // and app.peptideclients.com/mfa right after a correct OTP.
      const localHint = readMfaLocalHint(claims.auth_session_id);
      if (localHint) {
        setDecision('allow');
        return;
      }
      const mfaEnabled = Boolean(claims.required || prefs?.mfa_enabled);
      if (!mfaEnabled) {
        // 2FA not enabled. The central auth site owns the optional
        // "set up 2FA" nudge; this guard never prompts for setup (doing
        // so was the source of the duplicate prompt). Allow through.
        setDecision('allow');
        return;
      }
      // mfa_required = true. If verified_at exists, the 7-day window
      // check is server-side: auth-audit-hook + rpc_user_mfa_status
      // only return mfa_verified_at when an unrevoked, unexpired
      // user_mfa_sessions row matches the current JWT session_id.
      setDecision(claims.verified_at ? 'allow' : 'deny');
    }
    // NOTE: do NOT `setDecision('pending')` here. The initial state
    // is already `'pending'`, so bootstrap renders Loading exactly
    // once. Subsequent runs (genuine TOKEN_REFRESHED with a new
    // access_token that `sessionsEqual` lets through) re-check in
    // the background and keep showing the prior decision until the
    // new claims arrive. Resetting to `'pending'` here would render
    // <Loading /> for one frame on every legitimate token rotation
    // and unmount the entire matched child tree -- killing any
    // in-progress form state. See file-header writeup for the
    // tab-switch failure mode that motivated this.
    void check();
    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    if (decision !== 'deny') return;
    if (location.pathname === mfaPath) return;
    try {
      window.sessionStorage.setItem(
        MFA_RETURN_TO_STORAGE_KEY,
        `${location.pathname}${location.search}${location.hash}`,
      );
    } catch {
      // ignore storage failures in hardened browsers
    }
  }, [decision, location.pathname, location.search, location.hash, mfaPath]);

  if (session === undefined || decision === 'pending') {
    return <BootLoader />;
  }
  if (decision === 'deny') {
    return <Navigate to={mfaPath} state={{ from: location }} replace />;
  }
  return <Outlet />;
}
